import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import api from "../hooks/api";
import { API_URLS } from "../App";
import TenantSelector from "../components/common/TenantSelector";
import { useTenantSelection } from "../hooks/useTenantSelection";
import { CmsPage } from "../lib/cms/types";
import { extractApiError } from "../helpers/apiErrors";
import CmsPagesList from "../components/CmsAdmin/CmsPagesList";
import CmsPageEditor from "../components/CmsAdmin/CmsPageEditor";

const CmsAdminPage: React.FC = () => {
  const { profile, isAuthenticated } = useAuth();
  const { setModalScreen, setError, setSuccess } = useLayoutContext();

  const [userProfile, setUserProfile] = useState<ProfileAndSettings | null>(
    null,
  );
  const [profileLoading, setProfileLoading] = useState(true);
  const [pages, setPages] = useState<CmsPage[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);

  // ── Multi-tenant selection (ADR-021) ─────────────────────────────────────
  // CMS pages are per-tenant; switching tenant refetches the list.
  const { tenants, selectedTenant, selectTenant } = useTenantSelection();

  useEffect(() => {
    profile()
      .then((p) => {
        setUserProfile(p);
        setProfileLoading(false);
      })
      .catch(() => setProfileLoading(false));
  }, [profile]);

  const fetchPages = useCallback(async () => {
    setPagesLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: CmsPage[] }>(API_URLS.CMS_ADMIN);
      setPages(res.data.data || []);
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao carregar páginas CMS."));
    } finally {
      setPagesLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    if (!profileLoading && userProfile) {
      fetchPages();
    }
  }, [profileLoading, userProfile, fetchPages]);

  const handlePageUpdated = useCallback(
    (page: CmsPage) => {
      setPages((prev) => {
        const idx = prev.findIndex((p) => p.slug === page.slug);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = page;
          return next;
        }
        return [page, ...prev];
      });
      setSuccess(`Página "${page.title}" atualizada com sucesso!`);
    },
    [setSuccess],
  );

  const handlePageDeleted = useCallback(
    (page: CmsPage) => {
      setPages((prev) => prev.filter((p) => p.slug !== page.slug));
      setSuccess(`Página "${page.title}" apagada com sucesso!`);
    },
    [setSuccess],
  );

  const openEditor = useCallback(
    (page?: CmsPage) => {
      setModalScreen({
        state: "big-view",
        title: page ? `Editar: ${page.slug}` : "Nova página CMS",
        childrenControlOverflow: true,
        children: (
          <CmsPageEditor
            page={page}
            onSave={(saved) => {
              handlePageUpdated(saved);
              setModalScreen(null);
            }}
            setError={setError}
          />
        ),
        onClose: () => setModalScreen(null),
      });
    },
    [setModalScreen, handlePageUpdated, setError],
  );

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  if (profileLoading)
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        A carregar...
      </div>
    );

  if (!userProfile?.isAdmin()) return <Navigate to="/" replace />;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Gestão de Conteúdos CMS
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Gerir páginas de conteúdo do site público (eurosys.pt).
            </p>
          </div>
          <button
            onClick={() => openEditor()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            + Nova Página
          </button>
        </div>

        <div className="mb-6 max-w-xs">
          <TenantSelector
            tenants={tenants}
            value={selectedTenant}
            onChange={(t) => {
              selectTenant(t);
              fetchPages();
            }}
          />
        </div>

        <CmsPagesList
          pages={pages}
          loading={pagesLoading}
          onEdit={openEditor}
          onDelete={handlePageDeleted}
          setError={setError}
        />
      </div>
    </div>
  );
};

export default CmsAdminPage;
